import { useEffect, useMemo, useState } from "react";
import { Loader2, Inbox, Phone, Mail, Store } from "lucide-react";
import { listServiceRequests, updateServiceRequest, getAdminToken } from "@/lib/api";
import { toast } from "sonner";
import AdminHeader from "@/components/AdminHeader";
import AdminGate from "@/components/AdminGate";

export default function AdminRequests() {
  const [authed, setAuthed] = useState(!!getAdminToken());
  if (!authed) return <AdminGate onAuthed={() => setAuthed(true)} redirect="/admin/requests" />;
  return <AdminRequestsInner />;
}

// Sprint 3C — filter values. `all` shows everything; specific offer
// types match the backend `service_requests.offerType` enum verbatim
// so counting stays truthful. `other` catches legacy
// recommendation-driven rows (offerType is null/absent).
const FILTERS = [
  { value: "all", label: "All" },
  { value: "AI_VISIBILITY_INTEREST", label: "AI Visibility" },
  { value: "REVIEW_SYSTEM_INTEREST", label: "Review System" },
  { value: "other", label: "Other" },
];

export function matchesFilter(req, filter) {
  if (filter === "all") return true;
  if (filter === "other") {
    return !req.offerType ||
      (req.offerType !== "AI_VISIBILITY_INTEREST" &&
       req.offerType !== "REVIEW_SYSTEM_INTEREST");
  }
  return req.offerType === filter;
}

// Sprint 3C — deterministic count math surfaced as a pure function so
// the unit test can pin it. Mirrors what the component computes in its
// `useMemo`.
export function computeInterestCounts(rows) {
  const list = rows || [];
  return {
    ai:       list.filter((r) => r.offerType === "AI_VISIBILITY_INTEREST").length,
    review:   list.filter((r) => r.offerType === "REVIEW_SYSTEM_INTEREST").length,
    other:    list.filter((r) => !r.offerType).length,
    newTotal: list.filter((r) => r.status === "new").length,
    total:    list.length,
  };
}

function AdminRequestsInner() {
  const [busy, setBusy] = useState(false);
  const [requests, setRequests] = useState(null);
  const [filter, setFilter] = useState("all");

  const load = async () => {
    setBusy(true);
    try {
      const list = await listServiceRequests();
      setRequests(list);
    } catch (err) {
      toast.error("Couldn't load requests.", { description: err.message });
    } finally {
      setBusy(false);
    }
  };

  useEffect(() => { load(); }, []);

  const setStatus = async (id, status) => {
    try {
      await updateServiceRequest(id, status);
      setRequests((prev) => prev.map((r) => (r.id === id ? { ...r, status } : r)));
      toast.success(`Marked ${status.replace(/_/g, " ")}`);
    } catch {
      toast.error("Update failed.");
    }
  };

  // Sprint 3C — deterministic counts sourced from the same list we
  // display, so the numbers never drift from the underlying rows.
  const counts = useMemo(() => computeInterestCounts(requests), [requests]);

  const filtered = useMemo(
    () => (requests || []).filter((r) => matchesFilter(r, filter)),
    [requests, filter],
  );

  return (
    <div className="min-h-screen" style={{ background: "var(--ta-bg)" }} data-testid="admin-requests-page">
      <AdminHeader onRefresh={load} refreshing={busy} active="requests" />

      <main className="w-full max-w-screen-lg mx-auto px-5 sm:px-8 py-8 page-fade">
        <div className="text-xs uppercase tracking-widest font-semibold" style={{ color: "var(--ta-secondary)" }}>
          Incoming
        </div>
        <h1 className="font-display text-3xl sm:text-4xl font-semibold tracking-tight mt-1" style={{ color: "var(--ta-primary)" }}>
          Service requests
        </h1>

        {/* Sprint 3C — interest summary. No charts, just totals. */}
        {requests && (
          <div
            className="mt-5 grid grid-cols-1 sm:grid-cols-3 gap-3"
            data-testid="admin-requests-summary"
          >
            <SummaryStat
              label="AI Visibility Requests"
              value={counts.ai}
              testId="admin-requests-summary-ai"
            />
            <SummaryStat
              label="Review System Requests"
              value={counts.review}
              testId="admin-requests-summary-review"
            />
            <SummaryStat
              label="Total New Requests"
              value={counts.newTotal}
              testId="admin-requests-summary-new-total"
            />
          </div>
        )}

        {/* Sprint 3C — filter pills. */}
        {requests && requests.length > 0 && (
          <div
            className="mt-6 flex flex-wrap gap-2"
            data-testid="admin-requests-filter"
          >
            {FILTERS.map((f) => {
              const active = filter === f.value;
              return (
                <button
                  key={f.value}
                  onClick={() => setFilter(f.value)}
                  data-testid={`admin-requests-filter-${f.value.toLowerCase()}`}
                  className="text-xs font-semibold uppercase tracking-widest px-3 py-2 rounded-full transition-colors"
                  style={{
                    background: active ? "var(--ta-primary)" : "var(--ta-surface)",
                    color: active ? "white" : "var(--ta-text-secondary)",
                    border: `1px solid ${active ? "var(--ta-primary)" : "var(--ta-border)"}`,
                  }}
                >
                  {f.label}
                </button>
              );
            })}
          </div>
        )}

        {busy && !requests && (
          <div className="mt-10 flex items-center gap-2 text-sm" style={{ color: "var(--ta-text-secondary)" }}>
            <Loader2 size={16} className="animate-spin" /> Loading…
          </div>
        )}

        {requests && requests.length === 0 && (
          <div
            className="mt-10 p-10 rounded-3xl bg-white border text-center"
            style={{ borderColor: "var(--ta-border)" }}
            data-testid="admin-empty"
          >
            <div
              className="mx-auto w-14 h-14 rounded-2xl inline-flex items-center justify-center mb-3"
              style={{ background: "var(--ta-accent)", color: "var(--ta-primary)" }}
            >
              <Inbox size={22} strokeWidth={2.2} />
            </div>
            <div className="font-display text-xl font-semibold" style={{ color: "var(--ta-primary)" }}>Nothing waiting yet.</div>
            <p className="mt-1 text-sm" style={{ color: "var(--ta-text-secondary)" }}>New service requests will show up here.</p>
          </div>
        )}

        {requests && requests.length > 0 && filtered.length === 0 && (
          <div
            className="mt-6 p-6 rounded-2xl bg-white border text-sm text-center"
            style={{ borderColor: "var(--ta-border)", color: "var(--ta-text-secondary)" }}
            data-testid="admin-requests-empty-filter"
          >
            No requests match this filter yet.
          </div>
        )}

        {filtered.length > 0 && (
          <div className="mt-6 flex flex-col gap-3" data-testid="admin-requests-list">
            {filtered.map((r) => (
              <RequestRow key={r.id} req={r} onStatus={setStatus} />
            ))}
          </div>
        )}
      </main>
    </div>
  );
}

function SummaryStat({ label, value, testId }) {
  return (
    <div
      className="p-4 rounded-2xl bg-white border"
      style={{ borderColor: "var(--ta-border)" }}
      data-testid={testId}
    >
      <div
        className="text-[11px] uppercase tracking-widest font-semibold"
        style={{ color: "var(--ta-text-secondary)" }}
      >
        {label}
      </div>
      <div
        className="mt-1 text-2xl font-semibold"
        style={{ color: "var(--ta-primary)" }}
        data-testid={`${testId}-value`}
      >
        {value}
      </div>
    </div>
  );
}

const STATUS_LABEL = {
  new: "New",
  acknowledged: "Acknowledged",
  in_progress: "In progress",
  closed: "Closed",
};
const STATUS_COLOR = {
  new: { fg: "var(--ta-secondary)", bg: "#FCE6DE" },
  acknowledged: { fg: "var(--ta-primary)", bg: "var(--ta-accent)" },
  in_progress: { fg: "#8A5A00", bg: "#FFF3D6" },
  closed: { fg: "var(--ta-success)", bg: "var(--ta-success-bg)" },
};

const OFFER_TYPE_LABEL = {
  AI_VISIBILITY_INTEREST: "AI Visibility",
  REVIEW_SYSTEM_INTEREST: "Review System",
};

function RequestRow({ req, onStatus }) {
  const c = STATUS_COLOR[req.status] || STATUS_COLOR.new;
  const created = new Date(req.createdAt).toLocaleString(undefined, {
    month: "short", day: "numeric", hour: "numeric", minute: "2-digit",
  });
  const next = { new: "acknowledged", acknowledged: "in_progress", in_progress: "closed", closed: "closed" }[req.status];
  const offerLabel = OFFER_TYPE_LABEL[req.offerType];
  return (
    <div className="p-5 sm:p-6 rounded-2xl bg-white border" style={{ borderColor: "var(--ta-border)" }} data-testid={`admin-request-${req.id}`}>
      <div className="flex items-start justify-between gap-3 mb-3">
        <div>
          <div className="flex flex-wrap items-center gap-2 text-[11px] uppercase tracking-widest font-semibold" style={{ color: "var(--ta-secondary)" }}>
            <span>{req.recommendationTitle ? "About" : "Standalone"}</span>
            {offerLabel && (
              <span
                className="px-2 py-0.5 rounded-full"
                style={{
                  background: "var(--ta-surface-alt)",
                  color: "var(--ta-primary)",
                  border: "1px solid var(--ta-border)",
                }}
                data-testid={`admin-request-${req.id}-offer-tag`}
              >
                {offerLabel}
              </span>
            )}
          </div>
          <div className="font-display text-lg font-semibold mt-0.5" style={{ color: "var(--ta-primary)" }}>
            {req.recommendationTitle || "Direct service request"}
          </div>
        </div>
        <span
          className="text-[11px] uppercase tracking-widest font-semibold px-3 py-1.5 rounded-full shrink-0"
          style={{ background: c.bg, color: c.fg }}
        >
          {STATUS_LABEL[req.status]}
        </span>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 text-sm" style={{ color: "var(--ta-text)" }}>
        <div className="flex items-center gap-2"><Store size={15} style={{ color: "var(--ta-text-secondary)" }} /> {req.businessName}</div>
        <div className="flex items-center gap-2">{req.contactName}</div>
        <div className="flex items-center gap-2"><Mail size={15} style={{ color: "var(--ta-text-secondary)" }} /> <a href={`mailto:${req.email}`}>{req.email}</a></div>
        <div className="flex items-center gap-2"><Phone size={15} style={{ color: "var(--ta-text-secondary)" }} /> <a href={`tel:${req.phone}`}>{req.phone}</a></div>
      </div>

      {req.additionalNotes && (
        <div
          className="mt-3 p-3 rounded-xl text-sm"
          style={{ background: "var(--ta-surface-alt)", color: "var(--ta-text)" }}
        >
          {req.additionalNotes}
        </div>
      )}

      <div className="mt-4 flex items-center justify-between text-xs" style={{ color: "var(--ta-text-secondary)" }}>
        <span>Received {created}</span>
        {req.status !== "closed" && (
          <button
            onClick={() => onStatus(req.id, next)}
            data-testid={`admin-advance-${req.id}`}
            className="inline-flex items-center gap-1 h-9 px-4 rounded-full text-xs font-medium text-white"
            style={{ background: "var(--ta-primary)" }}
          >
            Mark {STATUS_LABEL[next]}
          </button>
        )}
      </div>
    </div>
  );
}
